import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Building2, Upload, FileText, Loader2, Save, Wand2, Info, Target, DollarSign, MapPin, XCircle } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import KnowledgeRepositorySection from "@/components/KnowledgeRepositorySection";
import { COMPANY_TYPES, getArchetypeLabel, FUNDING_STAGES, DEVELOPMENT_STAGES, INDICATIONS, MODALITIES, TARGET_FOCUS_OPTIONS, RUNWAY_OPTIONS, COMPANY_LOCATIONS, PRODUCT_TYPES, BUSINESS_MODELS, deriveCompanyArchetype } from "./profileConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [decks, setDecks] = useState([]);
  const [profile, setProfile] = useState({
    company_name: "", 
    // New multi-select fields
    product_types: [], // Multi-select
    business_models: [], // Multi-select
    primary_business_model: "", // Single select for primary
    archetype: "", // Derived label
    archetype_id: "", // Derived ID
    // Legacy field for backwards compatibility
    company_type: "",
    indications: [], 
    modality: "",
    funding_stage: "", 
    development_stage: "", 
    description: "", 
    one_liner: "",
    location: "",
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [profileRes, decksRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/company-profile`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/decks`, { credentials: "include" }),
      ]);
      if (profileRes.ok) {
        const data = await profileRes.json();
        if (data.profile) {
          let ind = data.profile.indications || (data.profile.indication ? [data.profile.indication] : []);
          
          // Load new fields or migrate from old format
          let productTypes = data.profile.product_types || [];
          let businessModels = data.profile.business_models || [];
          let primaryBusinessModel = data.profile.primary_business_model || "";
          
          // Migrate from old company_type if needed
          if (productTypes.length === 0 && data.profile.company_type) {
            const oldType = data.profile.company_type;
            if (oldType === "asset_generation") {
              productTypes = ["therapeutic_asset"];
              businessModels = ["internal_asset_dev"];
              primaryBusinessModel = "internal_asset_dev";
            } else if (oldType === "platform_service") {
              productTypes = ["computational_ai"];
              businessModels = ["fee_for_service", "platform_partnerships"];
              primaryBusinessModel = "fee_for_service";
            } else if (oldType === "internal_platform_asset" || oldType === "internal_external_platform_asset") {
              productTypes = ["therapeutic_asset", "computational_ai"];
              businessModels = ["internal_asset_dev", "platform_partnerships"];
              primaryBusinessModel = "internal_asset_dev";
            } else if (oldType === "enabling_technologies") {
              productTypes = ["enabling_technology"];
              businessModels = ["licensing_technology", "fee_for_service"];
              primaryBusinessModel = "licensing_technology";
            }
          }
          
          // Derive archetype
          const archetypeData = deriveCompanyArchetype(productTypes, businessModels, primaryBusinessModel);
          
          setProfile(p => ({ 
            ...p, 
            ...data.profile, 
            indications: Array.isArray(ind) ? ind : [ind],
            product_types: productTypes,
            business_models: businessModels,
            primary_business_model: primaryBusinessModel,
            archetype: archetypeData.label,
            archetype_id: archetypeData.archetype,
            company_type: data.profile.company_type || "",
            target_focus: data.profile.target_focus || "",
            target_name: data.profile.target_name || "",
            valuation_amount: data.profile.valuation_amount || "",
            valuation_year: data.profile.valuation_year || new Date().getFullYear().toString(),
            runway: data.profile.runway || "",
            location: data.profile.location || ""
          }));
        }
      }
      if (decksRes.ok) { const data = await decksRes.json(); setDecks(data.decks || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Toggle product type selection
  const toggleProductType = (typeId) => {
    setProfile(p => {
      const newTypes = p.product_types.includes(typeId)
        ? p.product_types.filter(t => t !== typeId)
        : [...p.product_types, typeId];
      const archetypeData = deriveCompanyArchetype(newTypes, p.business_models, p.primary_business_model);
      return {
        ...p,
        product_types: newTypes,
        archetype: archetypeData.label,
        archetype_id: archetypeData.archetype
      };
    });
  };

  // Toggle business model selection
  const toggleBusinessModel = (modelId) => {
    setProfile(p => {
      const newModels = p.business_models.includes(modelId)
        ? p.business_models.filter(m => m !== modelId)
        : [...p.business_models, modelId];
      // If removing the primary, reset it
      let newPrimary = p.primary_business_model;
      if (!newModels.includes(newPrimary)) {
        newPrimary = newModels[0] || "";
      }
      const archetypeData = deriveCompanyArchetype(p.product_types, newModels, newPrimary);
      return {
        ...p,
        business_models: newModels,
        primary_business_model: newPrimary,
        archetype: archetypeData.label,
        archetype_id: archetypeData.archetype
      };
    });
  };

  // Set primary business model
  const setPrimaryBusinessModel = (modelId) => {
    setProfile(p => {
      const archetypeData = deriveCompanyArchetype(p.product_types, p.business_models, modelId);
      return {
        ...p,
        primary_business_model: modelId,
        archetype: archetypeData.label,
        archetype_id: archetypeData.archetype
      };
    });
  };

  // Legacy function for backwards compatibility
  const selectCompanyType = (typeId) => {
    setProfile(p => ({
      ...p,
      company_type: typeId,
      archetype: getArchetypeLabel(typeId) || ""
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/company-profile`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(profile)
      });
      if (res.ok) {
        const data = await res.json();
        // Mark profile as completed so future logins land straight on Dashboard.
        try {
          await fetch(`${BACKEND_URL}/api/profile/complete`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            credentials: "include", body: JSON.stringify({ confirmed: true })
          });
        } catch {}
        toast.success("Profile saved — welcome to your dashboard");
        if (data.archetype_changed) {
          toast.info("Archetype changed - your diagnosis answers have been reset", { duration: 5000 });
        }
        setTimeout(() => navigate("/dashboard"), 400);
      } else {
        toast.error("Failed to save");
      }
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  // Called by KnowledgeRepositorySection after the AI extracts fields from uploaded docs.
  const mergeExtractedProfile = (extracted) => {
    if (!extracted || typeof extracted !== "object") return;
    setProfile((p) => {
      const updated = { ...p };
      if (extracted.company_name) updated.company_name = extracted.company_name;
      if (extracted.one_liner) updated.one_liner = extracted.one_liner;
      if (extracted.description) updated.description = extracted.description;
      if (extracted.modality) updated.modality = extracted.modality;
      if (extracted.funding_stage) updated.funding_stage = extracted.funding_stage;
      if (extracted.development_stage) updated.development_stage = extracted.development_stage;
      if (extracted.location) updated.location = extracted.location;
      if (Array.isArray(extracted.indications) && extracted.indications.length > 0) {
        updated.indications = extracted.indications.map((i) => String(i).toLowerCase());
      }
      if (Array.isArray(extracted.product_types) && extracted.product_types.length > 0) {
        updated.product_types = extracted.product_types;
      }
      if (Array.isArray(extracted.business_models) && extracted.business_models.length > 0) {
        updated.business_models = extracted.business_models;
      }
      if (extracted.primary_business_model) updated.primary_business_model = extracted.primary_business_model;
      if (updated.product_types.length > 0 && updated.primary_business_model) {
        const derived = deriveCompanyArchetype(updated.product_types, updated.business_models, updated.primary_business_model);
        updated.archetype_id = derived.archetype || derived.id;
        updated.archetype = derived.label;
      }
      return updated;
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(pdf|pptx|ppt)$/)) {
      toast.error("Please upload a PDF or PPTX file"); return;
    }
    setUploading(true); setUploadProgress(0);
    const formData = new FormData(); formData.append("file", file);
    try {
      const interval = setInterval(() => setUploadProgress(p => Math.min(p + 10, 90)), 200);
      const res = await fetch(`${BACKEND_URL}/api/decks/upload`, { method: "POST", credentials: "include", body: formData });
      clearInterval(interval); setUploadProgress(100);
      if (res.ok) {
        const d = await res.json();
        toast.success("Deck uploaded!");
        setDecks(prev => [{ deck_id: d.deck_id, filename: d.filename, status: d.status || "uploaded" }, ...prev]);
      } else { toast.error("Upload failed"); }
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false); setUploadProgress(0); e.target.value = '';
  };

  const handleExtract = async () => {
    if (!decks.length) { toast.error("Upload a deck first"); return; }
    setExtracting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/decks/${decks[0].deck_id}/extract-profile`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const { extracted } = await res.json();
        if (extracted && Object.keys(extracted).length > 0) {
          // Only update fields that were confidently extracted
          setProfile(p => {
            const updated = { ...p };
            
            // Basic fields - only update if extracted
            if (extracted.company_name) updated.company_name = extracted.company_name;
            if (extracted.one_liner) updated.one_liner = extracted.one_liner;
            if (extracted.description) updated.description = extracted.description;
            if (extracted.modality) updated.modality = extracted.modality;
            if (extracted.funding_stage) updated.funding_stage = extracted.funding_stage;
            if (extracted.development_stage) updated.development_stage = extracted.development_stage;
            if (extracted.location) updated.location = extracted.location;
            
            // Indications - array field
            if (extracted.indications && Array.isArray(extracted.indications) && extracted.indications.length > 0) {
              updated.indications = extracted.indications.map(i => i.toLowerCase());
            }
            
            // New multi-select fields
            if (extracted.product_types && Array.isArray(extracted.product_types) && extracted.product_types.length > 0) {
              updated.product_types = extracted.product_types;
            }
            if (extracted.business_models && Array.isArray(extracted.business_models) && extracted.business_models.length > 0) {
              updated.business_models = extracted.business_models;
            }
            if (extracted.primary_business_model) {
              updated.primary_business_model = extracted.primary_business_model;
            }
            
            // Derive archetype from new fields if they were extracted
            if (updated.product_types.length > 0 && updated.primary_business_model) {
              const derived = deriveCompanyArchetype(updated.product_types, updated.primary_business_model);
              updated.archetype_id = derived.id;
              updated.archetype = derived.label;
            }
            
            return updated;
          });
          
          const fieldsExtracted = Object.keys(extracted).length;
          toast.success(`Profile populated with ${fieldsExtracted} field${fieldsExtracted > 1 ? 's' : ''}!`);
        } else {
          toast.info("Could not extract profile information with confidence");
        }
      } else { toast.error("Extraction failed"); }
    } catch (e) { toast.error("Extraction failed"); }
    setExtracting(false);
  };

  const toggleInd = (ind) => {
    const lower = ind.toLowerCase();
    setProfile(p => ({
      ...p, indications: p.indications.includes(lower) ? p.indications.filter(i => i !== lower) : [...p.indications, lower]
    }));
  };

  if (loading) return <AppLayout>{() => <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}</AppLayout>;

  return (
    <AppLayout>
      {() => (
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Company Profile</h1>
            <p className="text-slate-600 mt-1">Upload everything about your company. Operon will read it all and pre-fill the fields below for you to check.</p>
          </div>
          <div className="space-y-6">
              <KnowledgeRepositorySection onExtracted={mergeExtractedProfile} />
              <Card className="border-2 border-dashed border-teal-300">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center"><Upload className="w-6 h-6 text-teal-600" /></div>
                    <div><h3 className="font-semibold">Upload Pitch Deck</h3><p className="text-sm text-slate-600">Upload and auto-fill profile</p></div>
                  </div>
                  <div className="flex gap-3">
                    <input type="file" id="deck-upload" accept=".pdf,.pptx,.ppt" onChange={handleFileUpload} className="hidden" />
                    <Button onClick={() => document.getElementById('deck-upload').click()} disabled={uploading} variant="outline" className="flex-1">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}{uploading ? "Uploading..." : "Choose File"}
                    </Button>
                    <Button onClick={handleExtract} disabled={extracting || !decks.length} className="btn-secondary">
                      {extracting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}{extracting ? "Extracting..." : "Auto-Fill"}
                    </Button>
                  </div>
                  {uploadProgress > 0 && <Progress value={uploadProgress} className="h-2" />}
                  {decks.length > 0 && <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4" />{decks[0].filename}<Badge variant="secondary">{decks[0].status}</Badge></div>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-600" />Company Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {/* 1. Company Name + Location */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input value={profile.company_name} onChange={e => setProfile(p => ({...p, company_name: e.target.value}))} placeholder="Your Company" />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-slate-500" />
                        Company HQ Location *
                      </Label>
                      <Select value={profile.location} onValueChange={v => setProfile(p => ({...p, location: v}))}>
                        <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                        <SelectContent>
                          {COMPANY_LOCATIONS.map(loc => (
                            <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 2. Indications */}
                  <div>
                    <Label>Indications *</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {INDICATIONS.map(ind => {
                        const sel = profile.indications.includes(ind.toLowerCase());
                        return (
                          <div key={ind} onClick={() => toggleInd(ind)} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${sel ? 'border-teal-500 bg-teal-50' : 'border-slate-200'}`}>
                            <Checkbox checked={sel} /><span className="text-sm">{ind}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 3. Modality */}
                  <div>
                    <Label>Modality *</Label>
                    <Select value={profile.modality} onValueChange={v => setProfile(p => ({...p, modality: v}))}>
                      <SelectTrigger><SelectValue placeholder="Select modality" /></SelectTrigger>
                      <SelectContent>{MODALITIES.map(m => <SelectItem key={m} value={m.toLowerCase()}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {/* 4. Product Type (multi-select) */}
                  <div>
                    <Label className="flex items-center gap-2">
                      Product Type *
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger><Info className="w-4 h-4 text-slate-400" /></TooltipTrigger>
                          <TooltipContent><p className="max-w-xs">Select all that apply to your company</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {PRODUCT_TYPES.map(type => (
                        <div
                          key={type.id}
                          onClick={() => toggleProductType(type.id)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            profile.product_types.includes(type.id)
                              ? "border-teal-500 bg-teal-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={profile.product_types.includes(type.id)} />
                            <span className="text-sm font-medium">{type.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 5. Business Model (multi-select with primary) */}
                  <div>
                    <Label className="flex items-center gap-2">
                      Business Model *
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger><Info className="w-4 h-4 text-slate-400" /></TooltipTrigger>
                          <TooltipContent><p className="max-w-xs">Select all revenue models, then choose your primary one</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {BUSINESS_MODELS.map(model => (
                        <div
                          key={model.id}
                          onClick={() => toggleBusinessModel(model.id)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            profile.business_models.includes(model.id)
                              ? "border-teal-500 bg-teal-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={profile.business_models.includes(model.id)} />
                            <span className="text-sm font-medium">{model.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Primary Business Model selector */}
                    {profile.business_models.length > 1 && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <Label className="text-blue-700 text-sm">Primary Business Model</Label>
                        <Select value={profile.primary_business_model} onValueChange={setPrimaryBusinessModel}>
                          <SelectTrigger className="mt-1"><SelectValue placeholder="Select primary model" /></SelectTrigger>
                          <SelectContent>
                            {profile.business_models.map(modelId => {
                              const model = BUSINESS_MODELS.find(m => m.id === modelId);
                              return model ? <SelectItem key={model.id} value={model.id}>{model.label}</SelectItem> : null;
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Derived Archetype Display */}
                  {profile.archetype && (
                    <div className="p-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200">
                      <Label className="text-teal-700 text-sm">Derived Company Type</Label>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge className="bg-teal-600 text-white">{profile.archetype}</Badge>
                        <span className="text-xs text-slate-500">Used for investor matching</span>
                      </div>
                    </div>
                  )}

                  {/* 6. Funding Stage and Development Stage */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Funding Stage *</Label>
                      <Select value={profile.funding_stage} onValueChange={v => setProfile(p => ({...p, funding_stage: v}))}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{FUNDING_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Development Stage *</Label>
                      <Select value={profile.development_stage} onValueChange={v => setProfile(p => ({...p, development_stage: v}))}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{DEVELOPMENT_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 6. One-Liner */}
                  <div>
                    <Label>One-Liner *</Label>
                    <Input value={profile.one_liner} onChange={e => setProfile(p => ({...p, one_liner: e.target.value}))} placeholder="What you do in one sentence" />
                  </div>

                  {/* 7. Description (optional) */}
                  <div>
                    <Label>Description <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Textarea value={profile.description} onChange={e => setProfile(p => ({...p, description: e.target.value}))} placeholder="Describe your company, technology, and approach" rows={4} />
                  </div>
                </CardContent>
              </Card>

              <Button onClick={handleSave} disabled={saving} className="w-full btn-secondary h-11" data-testid="btn-save-profile">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save profile & continue to Dashboard
              </Button>
            </div>
        </div>
      )}
    </AppLayout>
  );
}

export default Profile;
